import { useEffect, useState } from "react";
import PortalLayout from "@/pages/portal/PortalLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { User, Lock, Bell, Loader2, Save, PlayCircle } from "lucide-react";

const NOTIF_KEYS = [
  { k: "artwork", l: "Artwork approvals and revisions" },
  { k: "production", l: "Production tracker updates" },
  { k: "vault", l: "Brand vault additions" },
  { k: "support", l: "Support replies" },
  { k: "invoices", l: "Invoices and payments" },
  { k: "marketing", l: "Product news and marketing" },
];

const PortalProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const [prefs, setPrefs] = useState({});
  const [passForm, setPassForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const load = () => {
    setLoading(true);
    api.get("/portal/profile")
      .then((r) => {
        setProfile(r.data.user);
        setPrefs(r.data.user?.notification_prefs || {});
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!profile) return;
    setSavingProfile(true);
    try {
      const payload = {
        full_name: profile.full_name,
        company: profile.company,
        phone: profile.phone,
        billing_address: profile.billing_address,
        shipping_address: profile.shipping_address,
      };
      await api.patch("/portal/profile", payload);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSavingProfile(false); }
  };

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      await api.patch("/portal/notification-prefs", patch);
    } catch (err) {
      toast.error("Failed to update preferences");
    }
  };

  const changePassword = async () => {
    if (passForm.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (passForm.new_password !== passForm.confirm) return toast.error("Passwords don't match");
    setSavingPass(true);
    try {
      await api.post("/portal/change-password", {
        current_password: passForm.current_password,
        new_password: passForm.new_password,
      });
      toast.success("Password updated.");
      setPassForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally { setSavingPass(false); }
  };

  const restartTour = async () => {
    try {
      await api.post("/portal/onboarding", { status: "restart" });
      localStorage.removeItem("flowtalis_onboarding");
      toast.success("Tour will restart on your next visit to the Dashboard.");
    } catch { toast.error("Failed to restart tour"); }
  };

  if (loading || !profile) {
    return <PortalLayout><div className="ft-card fx-skeleton h-64" /></PortalLayout>;
  }

  return (
    <PortalLayout>
      <div className="mb-6">
        <div className="ft-eyebrow mb-2">Client Portal</div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="portal-profile-title">
          Profile & Settings
        </h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="ft-card">
            <div className="flex items-center gap-2 mb-4">
              <User size={16} className="text-[var(--ft-gold)]" />
              <h2 className="text-lg font-semibold tracking-tight">Company Information</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Full Name"><input className="ft-input" value={profile.full_name || ""} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} data-testid="portal-profile-name" /></Field>
              <Field label="Email"><input className="ft-input" value={profile.email || ""} disabled /></Field>
              <Field label="Company"><input className="ft-input" value={profile.company || ""} onChange={(e) => setProfile({ ...profile, company: e.target.value })} data-testid="portal-profile-company" /></Field>
              <Field label="Phone"><input className="ft-input" value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="portal-profile-phone" /></Field>
              <Field label="Billing Address">
                <textarea className="ft-input min-h-[80px]" value={profile.billing_address || ""} onChange={(e) => setProfile({ ...profile, billing_address: e.target.value })} data-testid="portal-profile-billing" />
              </Field>
              <Field label="Shipping Address">
                <textarea className="ft-input min-h-[80px]" value={profile.shipping_address || ""} onChange={(e) => setProfile({ ...profile, shipping_address: e.target.value })} data-testid="portal-profile-shipping" />
              </Field>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={save} disabled={savingProfile} className="ft-btn-primary" data-testid="portal-profile-save">
                {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Profile
              </button>
            </div>
          </div>

          <div className="ft-card">
            <div className="flex items-center gap-2 mb-4">
              <Lock size={16} className="text-[var(--ft-gold)]" />
              <h2 className="text-lg font-semibold tracking-tight">Change Password</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Current Password"><input type="password" className="ft-input" value={passForm.current_password} onChange={(e) => setPassForm({ ...passForm, current_password: e.target.value })} data-testid="portal-pass-current" /></Field>
              <Field label="New Password"><input type="password" className="ft-input" value={passForm.new_password} onChange={(e) => setPassForm({ ...passForm, new_password: e.target.value })} data-testid="portal-pass-new" /></Field>
              <Field label="Confirm"><input type="password" className="ft-input" value={passForm.confirm} onChange={(e) => setPassForm({ ...passForm, confirm: e.target.value })} data-testid="portal-pass-confirm" /></Field>
            </div>
            <div className="text-xs text-[var(--ft-muted)] mt-2">Min. 8 characters. Choose something you don&apos;t use elsewhere.</div>
            <div className="mt-4 flex justify-end">
              <button onClick={changePassword} disabled={savingPass} className="ft-btn-primary" data-testid="portal-pass-save">
                {savingPass ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Update Password
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="ft-card">
            <div className="flex items-center gap-2 mb-4">
              <Bell size={16} className="text-[var(--ft-gold)]" />
              <h2 className="text-lg font-semibold tracking-tight">Notifications</h2>
            </div>
            <div className="space-y-3">
              {NOTIF_KEYS.map((n) => {
                const enabled = prefs[n.k] !== false;
                return (
                  <label key={n.k} className="flex items-center justify-between gap-2 cursor-pointer" data-testid={`portal-pref-${n.k}`}>
                    <span className="text-sm">{n.l}</span>
                    <button
                      type="button"
                      onClick={() => savePrefs({ [n.k]: !enabled })}
                      className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? "bg-[var(--ft-gold)]" : "bg-[var(--ft-border)]"}`}
                    >
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="ft-card">
            <div className="flex items-center gap-2 mb-4">
              <PlayCircle size={16} className="text-[var(--ft-gold)]" />
              <h2 className="text-lg font-semibold tracking-tight">Product Tour</h2>
            </div>
            <p className="text-sm text-[var(--ft-muted)] mb-4">
              Re-run the 60-second walkthrough of your Client Success Portal.
            </p>
            <button onClick={restartTour} className="ft-btn-secondary" data-testid="portal-restart-tour">
              <PlayCircle size={14} /> Restart Tour
            </button>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="ft-label block mb-1.5">{label}</label>
    {children}
  </div>
);

export default PortalProfile;
